import { useEffect, useState } from 'react';

import { MenuItem } from '@mui/material';
import { useHistory } from 'react-router';

import { VoiceContent } from 'domains/content/types';
import Autocomplete from 'domains/core/components/Autocomplete';
import CreateNewDropDownItem from 'domains/core/components/CreateNewDropDownItem';
import useVoiceContentList from 'hooks/queries/useVoiceContentList';
import { URLPaths } from 'models/enums';

import { AutocompleteOption } from '../Autocomplete/Autocomplete';

import ButtonVoicePreview from './ButtonVoicePreview';

const createNewOption = { index: 0, value: undefined as any, label: 'Create New' };
export type Props = {
    onChange: (e: any, option: AutocompleteOption<VoiceContent>) => void;
    onVoicePreviewClick: () => void;
    onInputChange: (e: any, inputValue: string) => void;
    isPreviewVisible: boolean;
    voiceContentInputValue: string;
    value: AutocompleteOption<VoiceContent>;
    // When true, shows "Unavailable" placeholder with error state (e.g., content was unpublished)
    isContentUnavailable?: boolean;
};

const AutocompleteVoiceCallContent = ({
    onChange,
    onVoicePreviewClick,
    onInputChange,
    isPreviewVisible,
    voiceContentInputValue,
    value,
    isContentUnavailable = false,
}: Props) => {
    const history = useHistory();
    const [options, setOptions] = useState<AutocompleteOption<number>[]>([createNewOption]);
    const { data: contentOptionList, isLoading } = useVoiceContentList();

    useEffect(() => {
        if (!isLoading) {
            let res = contentOptionList?.map((voiceContent: VoiceContent, index: number) => {
                let obj = {
                    index: index + 1,
                    value: voiceContent,
                    label: voiceContent.name,
                };
                return obj;
            });
            if (res) setOptions([createNewOption, ...res]);
        }
    }, [isLoading, contentOptionList]);

    return (
        <Autocomplete
            freeSolo={false}
            fullWidth
            getOptionLabel={(option) => option.label ?? ''}
            inputValue={voiceContentInputValue ?? ''}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            label="Voice Content"
            leadingIcon={
                voiceContentInputValue ? (
                    <ButtonVoicePreview onClick={onVoicePreviewClick} isActive={isPreviewVisible} />
                ) : null
            }
            onChange={onChange}
            onInputChange={onInputChange}
            options={options ?? []}
            placeholder={isContentUnavailable ? 'Unavailable' : 'Select or create content'}
            error={isContentUnavailable}
            renderOption={({ className, ...rest }, { index, label, value }) => {
                const shouldRenderTopOption = index === 0;

                return shouldRenderTopOption ? (
                    <CreateNewDropDownItem
                        {...rest}
                        children={label}
                        key={index}
                        value={value}
                        onClick={() => {
                            history.push(URLPaths.CONTENT_CREATE_VOICE_CALL);
                        }}
                    />
                ) : (
                    <MenuItem {...rest} children={label} key={value.id} value={value} />
                );
            }}
            type="string"
            value={value ?? null}
        />
    );
};

export default AutocompleteVoiceCallContent;
